import React from 'react'
import { Component, LocaleCode } from '../types/component'
import { getLocalizedText } from '../utils/localization'
import { ComponentRenderer } from './ComponentRenderer'
import { cn } from '../lib/utils'

interface CardProps {
  component: Component
  locale: LocaleCode
  storeId: string
  apiUrl: string
  style: React.CSSProperties
}

export function Card({ component, locale, storeId, apiUrl, style }: CardProps) {
  const config = component.config as any

  const title = getLocalizedText(config.title, locale)
  const content = getLocalizedText(config.content, locale)
  const imageUrl = config.imageUrl
  const imagePosition = config.imagePosition || 'top'

  const isImageBackground = imagePosition === 'background'
  const isImageSide = imagePosition === 'left' || imagePosition === 'right'

  const containerStyle: React.CSSProperties = {
    ...style,
  }

  const textColor = isImageBackground ? '#ffffff' : style.color

  return (
    <div
      style={containerStyle}
      className={cn('w-full rounded-lg overflow-hidden shadow-md', isImageBackground && 'relative')}
    >
      {isImageBackground && imageUrl && (
        <>
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${imageUrl})` }}
          />
          <div className="absolute inset-0 bg-black opacity-40" />
        </>
      )}

      <div className={cn(
        isImageBackground && 'relative z-10 p-8',
        isImageSide && 'flex gap-6 items-start'
      )}>
        {imageUrl && imagePosition === 'left' && (
          <img
            src={imageUrl}
            alt={title || 'Card image'}
            className="w-1/3 rounded-lg object-cover"
          />
        )}

        {imageUrl && imagePosition === 'top' && (
          <img
            src={imageUrl}
            alt={title || 'Card image'}
            className="w-full h-48 object-cover mb-4"
          />
        )}

        <div className={isImageSide ? 'flex-1' : ''}>
          {title && (
            <h3 className="text-2xl font-bold mb-3" style={{ color: textColor }}>
              {title}
            </h3>
          )}

          {content && (
            <p
              className="mb-4"
              style={{
                color: textColor,
                opacity: isImageBackground ? 0.9 : 0.8
              }}
            >
              {content}
            </p>
          )}

          {component.children && component.children.length > 0 && (
            <div className="flex gap-3">
              {component.children.map((child) => (
                <ComponentRenderer
                  key={child.id}
                  component={child}
                  locale={locale}
                  storeId={storeId}
                  apiUrl={apiUrl}
                />
              ))}
            </div>
          )}
        </div>

        {imageUrl && imagePosition === 'right' && (
          <img
            src={imageUrl}
            alt={title || 'Card image'}
            className="w-1/3 rounded-lg object-cover"
          />
        )}
      </div>
    </div>
  )
}
